import { BookOpenText, Minimize2, SlidersHorizontal, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useUiMode, type UiMode } from "@/lib/uiMode";

/**
 * UI-16's one control: which of the two surfaces this account sees.
 *
 * The two cards make the same promise in both directions, which is the point of drawing
 * them identically rather than as a switch with an "advanced" label: neither mode is a
 * degraded version of the other, and **nothing is deleted by choosing one**. That sentence
 * is in the footer because it is the actual worry — somebody who has spent a month feeding
 * the pipeline is being offered a screen that hides most of it.
 *
 * Switching writes `user_settings.ui_mode` and nothing else. The rail, the default page and
 * every route's rendering follow from that one value, so there is no second place for the
 * two to disagree, and the choice survives a reload and a different browser because it is
 * about the person rather than the device.
 *
 * **Switching from here leaves you here.** The handoff is explicit, and the reason is that
 * a mode switch that also navigated would answer a question nobody asked: somebody in
 * Settings is configuring, not going somewhere. What the switch does do is change what is
 * around them — this section is one of the four simple mode keeps, so the page they are
 * reading is still there when the rail beside it has been rewritten.
 */
export function ModeSettings({ summary }: { summary: string }) {
  const { updateSettings } = useApp();
  const mode = useUiMode();

  const [busy, setBusy] = useState<UiMode | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function choose(next: UiMode) {
    if (busy || next === mode) return;
    setBusy(next);
    setProblem(null);
    try {
      await updateSettings({ uiMode: next });
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Mode
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      <div className="flex flex-col gap-[var(--space-4)]">
        {MODES.map((card) => (
          <ModeCard
            key={card.id}
            card={card}
            active={mode === card.id}
            busy={busy === card.id}
            onChoose={() => void choose(card.id)}
          />
        ))}
      </div>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)] [text-wrap:pretty]">
        {mode === "simple"
          ? "Simple mode is on. Nothing is deleted — everything is still there when you switch back."
          : "Full mode is on. Simple mode keeps the same data and hides everything except adding, answering and reading."}
      </p>

      {mode === "simple" && (
        <div>
          <Link
            to="/world-view"
            className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)]"
          >
            <BookOpenText size={13} strokeWidth={2} />
            Open the world view
          </Link>
        </div>
      )}
    </div>
  );
}

interface ModeCardSpec {
  id: UiMode;
  title: string;
  icon: LucideIcon;
  summary: string;
  points: string[];
}

/**
 * The handoff's two cards, word for word.
 *
 * Full mode's last bullet is the one that matters: "Everything simple mode has". The two
 * lists are not a trade — one is a subset of the other — and saying so is what stops the
 * choice reading like a feature tier.
 */
const MODES: ModeCardSpec[] = [
  {
    id: "simple",
    title: "Simple",
    icon: Minimize2,
    summary: "Drop things in, answer what only you can answer, read the world view.",
    points: [
      "Add a source — audio, documents, notes",
      "Needs you — one question at a time",
      "World view — what is currently believed",
      "Ask and search",
    ],
  },
  {
    id: "full",
    title: "Full",
    icon: SlidersHorizontal,
    summary: "Every stage of the pipeline, open for inspection and tuning.",
    points: [
      "Recordings, transcripts and the processing pipeline",
      "Insights ledger, people and entities",
      "Engines, providers and the worker",
      "Everything simple mode has",
    ],
  },
];

function ModeCard({
  card,
  active,
  busy,
  onChoose,
}: {
  card: ModeCardSpec;
  active: boolean;
  busy: boolean;
  onChoose: () => void;
}) {
  const Icon = card.icon;

  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={active}
      disabled={busy}
      className={cn(
        "flex flex-col gap-3 rounded-[var(--radius-xl)] border bg-[var(--surface-glass)] p-[var(--space-4)] text-left backdrop-blur-[var(--blur-sm)] transition disabled:opacity-60",
        active ? "border-[var(--primary)]" : "border-[var(--border-subtle)] hover:border-[var(--border-strong)]",
      )}
      style={active ? { boxShadow: "0 0 0 3px color-mix(in oklch, var(--primary) 22%, transparent)" } : undefined}
    >
      <span className="flex items-center gap-2.5">
        <Icon size={16} strokeWidth={2} className="text-[var(--vl-virtual-soft)]" aria-hidden />
        <span className="text-[length:var(--text-xl)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          {card.title}
        </span>
        <span
          className="ml-auto whitespace-nowrap rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold"
          style={
            active
              ? { background: "color-mix(in oklch, var(--primary) 18%, transparent)", color: "var(--primary)" }
              : { color: "var(--text-muted)" }
          }
        >
          {active ? "On" : busy ? "Switching…" : `Switch to ${card.title.toLowerCase()}`}
        </span>
      </span>

      <span className="text-[length:var(--text-md)] leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]">
        {card.summary}
      </span>

      <span className="flex flex-col gap-1.5">
        {card.points.map((point) => (
          <span key={point} className="flex items-center gap-2 text-[length:var(--text-xs)] text-[var(--text-muted)]">
            <span aria-hidden className="size-1 shrink-0 rounded-full bg-[var(--border-strong)]" />
            {point}
          </span>
        ))}
      </span>
    </button>
  );
}
